import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { BILLING_ENABLED } from 'reactApp/appHelpers/configHelpers';
import { abHeavyFileBuy } from 'reactApp/appHelpers/featuresHelpers/features/heavyFileBuy';
import { loadPaidInfo } from 'reactApp/modules/paidInfo/paidInfo.saga';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { productsController } from 'reactApp/modules/products/products.controller';
import { sendGaUploaderNew } from 'reactApp/modules/uploading/helpers/uploading.analytics';
import type { UploadingDescriptor } from 'reactApp/modules/uploading/serviceClasses/UploadingDescriptor';
import { uploadingService } from 'reactApp/modules/uploading/serviceClasses/UploadingService';
import { ERetryErrorFileOptions } from 'reactApp/modules/uploading/uploading.types';
import { EFileError, EFileStatus } from 'reactApp/modules/uploadList/uploadList.model';
import { updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUser } from 'reactApp/modules/user/user.thunkActions';
import { channel } from 'redux-saga';
import { put, take } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

let waitChannel;

const getFilesSizeBytes = (filesOverLimit) => filesOverLimit.reduce((resSize, descriptor) => resSize + descriptor.size, 0);

const getFilesSize = (size) => bytesToNDigits(size, 3).value;

export function* handleFileSizeLimitModal({
    descriptor,
    filesOverLimit,
    uploadLimit,
}: {
    descriptor: UploadingDescriptor;
    filesOverLimit: UploadingDescriptor[];
    uploadLimit: number;
}) {
    const fileSize = descriptor.size;
    const descriptorId = descriptor.id;
    const cloudPath = descriptor.cloudPath;

    const retryResult: ERetryErrorFileOptions = ERetryErrorFileOptions.cancel;

    if (!waitChannel) {
        waitChannel = channel();
    } else {
        return ERetryErrorFileOptions.shouldRetry;
    }

    let result: any = {
        file: {
            extension: descriptor.nameParts?.extension,
            name: descriptor.cloudName,
            sizeBytes: fileSize,
        },
    };

    if (filesOverLimit.length > 1) {
        const sizeOverLimit = getFilesSizeBytes(filesOverLimit);

        result = {
            files: {
                size: getFilesSize(sizeOverLimit),
                sizeBytes: sizeOverLimit,
                number: filesOverLimit.length,
            },
            // Добавляем инфу о первом файле выше лимита.
            file: {
                extension: descriptor.nameParts?.extension,
                name: descriptor.cloudName,
                sizeBytes: descriptor.size,
            },
        };
    }

    yield put(
        updateUploadFilesAction({
            descriptorId,
            cloudPath,
            status: EFileStatus.WARNING,
            error: EFileError.OVER_QUOTA_LIMIT_DOWNLOAD,
        })
    );

    if (BILLING_ENABLED) {
        /* tempexp_17604-next-line*/
        yield loadPaidInfo();

        // дальше будем предлагать разные тарифы, поэтому грузим продукты, если ещё нет
        /* tempexp_18869-next-line tempexp_18955-next-line */
        yield productsController.loadProducts();
    }

    openPopupHelper({
        popupName: popupNames.UPLOAD_BIG_FILE,
        data: {
            ...result,
            numberOfFiles: uploadingService.queueForUpload.length,
            skipFileLoading: () => {
                sendGaUploaderNew('file', 'over_quota_limit_download');

                const actions = _skipUserFilesOverLimit(filesOverLimit, uploadLimit);
                waitChannel.put(actions);
            },
            continueFileLoading: () => {
                waitChannel.put(true);
            },
        },
    });

    const actions = yield take(waitChannel);
    waitChannel.close();
    waitChannel = null;

    if (Array.isArray(actions)) {
        for (const action of actions) {
            yield put(action);
        }
    } else if (actions === true) {
        yield loadUser();

        // После того, как юзер совершил оплату и данные обновились - получаем инфу о новом лимите и обновляем в текущем дескрипторе.
        const updatedUserLimit = (yield* select(UserSelectors.getUserData)).uploadLimit;
        descriptor.uploadingPacketConfig.setUserFileSizeLimit(updatedUserLimit);

        // Сбрасываем состояние ошибки в GUI списка загрузок
        yield put(
            updateUploadFilesAction({
                descriptorId,
                cloudPath,
                status: EFileStatus.PAUSED,
                error: '',
                hideError: true,
            })
        );
        return ERetryErrorFileOptions.shouldRetryImmediately;
    }

    return retryResult;
}

function _skipUserFilesOverLimit(filesOverLimit: UploadingDescriptor[], uploadLimit: number) {
    const actions: any[] = [];
    filesOverLimit.forEach((descriptor) => {
        const fileSize = descriptor.size;

        if (fileSize >= uploadLimit) {
            /* tempexp_18869-next-line */
            const error = abHeavyFileBuy ? EFileError.HEAVY_FILE_BUY : EFileError.CANCELLED_FILE;

            actions.push(
                updateUploadFilesAction({
                    descriptorId: descriptor.id,
                    cloudPath: descriptor.cloudPath,
                    status: EFileStatus.CANCEL,
                    error,
                    hideError: true,
                })
            );
            uploadingService.cancel(descriptor.id);
        }
    });

    return actions;
}
